import { useEffect, useMemo, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  LoadingSkeleton,
  Modal,
  Select,
  useToast,
} from '@nexora/design-system';
import { useBoms, type Bom } from './useBoms';
import { useBomDetail, useSetBomLabor, type BomLaborLine } from './useBomDetails';
import { formatCurrency } from '../../shared/utils/format';

/* ── Contrato (backend: bom-details.controller.ts + manufacturing.dto.ts) ────
 *
 * GET /manufacturing/boms/:id -> BomResponseDto { id, productId, version, description,
 *   status, components[{ productId, productName?, sku?, quantity, unit, unitCost, wastePercent, notes? }],
 *   labor?: [{ description, type, qtyHours, costRate, totalCost, leadTimeDays? }], totalCost, ... }
 * PUT /manufacturing/boms/:id/labor body: { labor: BomLaborLineDto[] }  (substitui as atuais)
 *   -> { bomId, labor, laborCost }
 *
 * Tela prototipo "BOM com Gestao de Mao de Obra": tabela Level/Component | Type | Qty |
 * UoM | Unit Cost | Ext. Cost | Lead Time + secao de mao de obra e operacoes.
 *
 * Fidelidade: o backend publica a BOM como lista plana (components[] + labor[]), sem
 * arvore multinivel do prototipo — as linhas sao renderizadas planas com o mesmo
 * conjunto de colunas. Lead time existe apenas nas linhas de mao de obra.
 */

const BOM_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'neutral' | 'error'> = {
  ACTIVE: 'success',
  DRAFT: 'neutral',
  OBSOLETE: 'error',
};

const BOM_STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Ativo',
  DRAFT: 'Rascunho',
  OBSOLETE: 'Obsoleto',
};

interface LaborRow {
  description: string;
  type: string;
  qtyHours: number;
  costRate: number;
  leadTimeDays?: number | null;
}

const emptyLaborRow = (): LaborRow => ({
  description: '',
  type: 'EXECUCAO',
  qtyHours: 1,
  costRate: 0,
  leadTimeDays: null,
});

export function BomLaborPage(): JSX.Element {
  const { addToast } = useToast();
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  const routeId = params['bomId'] ?? params['id'];

  const [selectedId, setSelectedId] = useState<string>(routeId ?? '');
  useEffect(() => {
    if (routeId) setSelectedId(routeId);
  }, [routeId]);

  const { data: bomsData } = useBoms({ page: 1, limit: 100 });
  const boms = bomsData?.data ?? [];

  const { data: bom, isLoading, error, refetch } = useBomDetail(selectedId || undefined);

  const setLabor = useSetBomLabor();

  const [editOpen, setEditOpen] = useState(false);
  const [rows, setRows] = useState<LaborRow[]>([]);

  const openEdit = (): void => {
    setRows((bom?.labor ?? []).map((l) => ({
      description: l.description,
      type: l.type,
      qtyHours: l.qtyHours,
      costRate: l.costRate,
      leadTimeDays: l.leadTimeDays ?? null,
    })));
    setEditOpen(true);
  };

  const handleSave = async (): Promise<void> => {
    if (!selectedId) return;
    const cleaned: BomLaborLine[] = rows
      .filter((r) => r.description.trim() !== '')
      .map((r) => ({
        description: r.description.trim(),
        type: r.type,
        qtyHours: Number(r.qtyHours) || 0,
        costRate: Number(r.costRate) || 0,
        totalCost: (Number(r.qtyHours) || 0) * (Number(r.costRate) || 0),
        leadTimeDays: r.leadTimeDays != null ? Number(r.leadTimeDays) : null,
      }));
    if (cleaned.length === 0) {
      addToast('Adicione ao menos uma linha de mão de obra', 'error');
      return;
    }
    try {
      await setLabor.mutateAsync({ id: selectedId, labor: cleaned });
      addToast('Mão de obra atualizada', 'success');
      setEditOpen(false);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro ao salvar mão de obra', 'error');
    }
  };

  const laborCost = useMemo(
    () => (bom?.labor ?? []).reduce((s, l) => s + l.totalCost, 0),
    [bom],
  );

  const rowsTotal = useMemo(
    () =>
      (bom?.components ?? []).reduce((s, c) => s + c.quantity * c.unitCost, 0) + laborCost,
    [bom, laborCost],
  );

  return (
    <div className="bom-labor-page">
      <style>{`
        .bom-labor-page { padding: 1.5rem; }
        .bom-labor-table-wrap { overflow-x: auto; }
        .bom-labor-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; min-width: 800px; }
        .bom-labor-table th { text-align: left; padding: 0.5rem 0.75rem; font-weight: 600; color: #43474e; border-bottom: 1px solid #c4c6cf; background: #eceef0; white-space: nowrap; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.03em; }
        .bom-labor-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #e0e3e5; }
        .bom-labor-num { text-align: right; font-variant-numeric: tabular-nums; font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .bom-labor-empty { padding: 2rem 1rem; text-align: center; color: #43474e; }
        .bom-labor-error { padding: 1.5rem; text-align: center; color: #ba1a1a; }
        .bom-labor-editor { display: flex; flex-direction: column; gap: 0.75rem; }
        .bom-labor-editor-row { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr 1fr auto; gap: 0.5rem; align-items: end; }
        .bom-labor-summary { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 0; border-top: 1px solid #e0e3e5; }
      `}</style>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="font-label-sm text-label-sm text-on-surface-variant mb-xs uppercase tracking-wider">
            Produção / Estrutura de Produto (BOM)
          </p>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Mão de Obra e Operações
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Operações (horas por unidade) e componentes da estrutura de produto.
          </p>
        </div>
        {bom && (
          <Badge variant={BOM_STATUS_VARIANT[bom.status] ?? 'neutral'} size="md">
            {BOM_STATUS_LABEL[bom.status] ?? bom.status}
          </Badge>
        )}
      </div>

      {!routeId && (
        <Card variant="outlined" padding="md" className="mb-lg">
          <div style={{ minWidth: '280px', maxWidth: '480px' }}>
            <Select
              label="Estrutura (BOM)"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              <option value="">— Selecionar —</option>
              {boms.map((b: Bom) => (
                <option key={b.id} value={b.id}>
                  {b.description} (v{b.version})
                </option>
              ))}
            </Select>
          </div>
        </Card>
      )}

      {isLoading && (
        <Card padding="1rem">
          <LoadingSkeleton count={6} height="48px" />
        </Card>
      )}

      {error && !isLoading && (
        <Card padding="2rem">
          <div className="bom-labor-error" role="alert">
            <p>Erro ao carregar a estrutura</p>
            <p className="font-body-sm text-body-sm text-on-surface-variant">{(error as Error).message}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        </Card>
      )}

      {!isLoading && !error && !bom && (
        <Card padding="2rem">
          <div className="bom-labor-empty">
            {selectedId ? 'Estrutura não encontrada.' : 'Nenhuma estrutura selecionada.'}
          </div>
        </Card>
      )}

      {bom && (
        <Card padding="0">
          <CardHeader
            className="px-md pt-md"
            action={
              <Button size="sm" variant="outline" onClick={openEdit}>
                Editar Mão de Obra
              </Button>
            }
          >
            <div>
              <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">
                {bom.description}
              </h3>
              <p className="font-label-md text-label-md text-on-surface-variant mt-xs m-0">
                v{bom.version} · SKU {bom.productId.slice(0, 8)}
              </p>
            </div>
          </CardHeader>
          <div className="bom-labor-table-wrap">
            <table className="bom-labor-table">
              <thead>
                <tr>
                  <th>Level / Component</th>
                  <th>Type</th>
                  <th style={{ textAlign: 'right' }}>Qty</th>
                  <th>UoM</th>
                  <th style={{ textAlign: 'right' }}>Unit Cost</th>
                  <th style={{ textAlign: 'right' }}>Ext. Cost</th>
                  <th style={{ textAlign: 'center' }}>Lead Time</th>
                </tr>
              </thead>
              <tbody>
                {/* Componentes (MATERIAL) */}
                {(bom.components ?? []).map((c, i) => (
                  <tr key={`${c.productId}-${i}`}>
                    <td>
                      <span className="font-medium text-on-surface">
                        {c.productName ?? c.productId}
                      </span>
                      <span className="font-label-sm text-label-sm text-on-surface-variant ml-xs">
                        {c.sku ? `(${c.sku})` : ''}
                      </span>
                    </td>
                    <td>
                      <span
                        className="font-label-sm text-label-sm"
                        style={{
                          background: '#eceef0',
                          color: '#43474e',
                          padding: '2px 6px',
                          borderRadius: 4,
                        }}
                      >
                        Raw Material
                      </span>
                    </td>
                    <td className="bom-labor-num">{c.quantity}</td>
                    <td className="text-on-surface-variant">{c.unit}</td>
                    <td className="bom-labor-num">{formatCurrency(c.unitCost)}</td>
                    <td className="bom-labor-num font-medium text-primary">
                      {formatCurrency(c.quantity * c.unitCost)}
                    </td>
                    <td style={{ textAlign: 'center' }} className="text-on-surface-variant">—</td>
                  </tr>
                ))}
                {/* Mao de obra (LABOR) */}
                {(bom.labor ?? []).map((l, i) => (
                  <tr key={`labor-${i}`}>
                    <td className="font-medium text-on-surface">{l.description}</td>
                    <td>
                      <span
                        className="font-label-sm text-label-sm"
                        style={{
                          background: '#d0e1fb',
                          color: '#001b3c',
                          padding: '2px 6px',
                          borderRadius: 4,
                        }}
                      >
                        {l.type} · Labor
                      </span>
                    </td>
                    <td className="bom-labor-num">{l.qtyHours}</td>
                    <td className="text-on-surface-variant">h</td>
                    <td className="bom-labor-num">{formatCurrency(l.costRate)}</td>
                    <td className="bom-labor-num font-medium text-primary">
                      {formatCurrency(l.totalCost)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {l.leadTimeDays != null ? `${l.leadTimeDays}d` : '—'}
                    </td>
                  </tr>
                ))}
                {bom.components.length === 0 && (bom.labor ?? []).length === 0 && (
                  <tr>
                    <td colSpan={7} className="bom-labor-empty">
                      Esta estrutura não tem componentes nem mão de obra registrados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-md pb-md">
            <div className="bom-labor-summary">
              <span className="font-body-sm text-body-sm text-on-surface-variant">
                Custo material: {formatCurrency((bom.components ?? []).reduce((s, c) => s + c.quantity * c.unitCost, 0))}
                {' · '}
                Mão de obra: {formatCurrency(laborCost)}
              </span>
              <span className="font-label-md text-label-md text-on-surface font-semibold">
                Custo total: {formatCurrency(bom.totalCost ?? rowsTotal)}
              </span>
            </div>
          </div>
        </Card>
      )}

      {/* Editor de mao de obra (PUT /boms/:id/labor — substitui as linhas atuais) */}
      <Modal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        title="Editar Mão de Obra"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} loading={setLabor.isPending}>Salvar</Button>
          </>
        }
      >
        <div className="bom-labor-editor">
          <p className="font-body-sm text-body-sm text-on-surface-variant">
            A atualização substitui todas as linhas de mão de obra da estrutura.
          </p>
          {rows.map((row, idx) => (
            <div key={idx} className="bom-labor-editor-row">
              <Input
                label="Operação"
                placeholder="Ex: Usinagem CNC"
                value={row.description}
                onChange={(e) =>
                  setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, description: e.target.value } : r)))
                }
              />
              <Select
                label="Tipo"
                value={row.type}
                onChange={(e) =>
                  setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, type: e.target.value } : r)))
                }
              >
                <option value="EXECUCAO">Execução</option>
                <option value="SETUP">Setup</option>
                <option value="INSPECAO">Inspeção</option>
                <option value="EMBALAGEM">Embalagem</option>
                <option value="OUTROS">Outros</option>
              </Select>
              <Input
                label="Horas"
                type="number"
                min="0"
                step="0.01"
                value={row.qtyHours}
                onChange={(e) =>
                  setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, qtyHours: Number(e.target.value) } : r)))
                }
              />
              <Input
                label="Custo/h (R$)"
                type="number"
                min="0"
                step="0.01"
                value={row.costRate}
                onChange={(e) =>
                  setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, costRate: Number(e.target.value) } : r)))
                }
              />
              <Input
                label="Lead time (dias)"
                type="number"
                min="0"
                value={row.leadTimeDays ?? ''}
                onChange={(e) =>
                  setRows((rs) =>
                    rs.map((r, i) =>
                      i === idx ? { ...r, leadTimeDays: e.target.value === '' ? null : Number(e.target.value) } : r,
                    ),
                  )
                }
              />
              <Button
                size="sm"
                variant="danger"
                onClick={() => setRows((rs) => rs.filter((_, i) => i !== idx))}
              >
                Remover
              </Button>
            </div>
          ))}
          <Button variant="outline" onClick={() => setRows((rs) => [...rs, emptyLaborRow()])}>
            + Adicionar linha
          </Button>
        </div>
      </Modal>
    </div>
  );
}